"use client"

import Image from "next/image"
import { useTranslations } from "next-intl"
import { cn } from "@/shared/lib/utils"

import {
  BODY_TYPE_PREVIEW_VIDEOS,
  BREAST_SIZE_PREVIEW_VIDEOS,
  BUTT_SIZE_PREVIEW_VIDEOS,
  ETHNICITY_PREVIEW_VIDEOS,
  type WizardVideoMap,
  type WizardVideoSources,
} from "@/features/create/lib/wizard-preview-videos"
import { WizardPreviewVideo } from "./wizard-preview-video"


// Image type: nested by Gender → Style
export type GS = { Male: { Anime: string; Realistic: string }; Female: { Anime: string; Realistic: string } }
export type FemaleOnly = { Female: { Anime: string; Realistic: string } }

export function resolveVideo(
  videos: WizardVideoMap | undefined,
  gender: string,
  style: string
): WizardVideoSources | null {
  if (!videos || gender === "male") return null
  const sKey = style === "anime" ? "Anime" : "Realistic"
  return videos.Female[sKey] ?? null
}

export function resolveImg(images: GS | FemaleOnly, gender: string, style: string): string {
  const gKey = gender === "male" ? "Male" : "Female" // trans → Female
  const sKey = style === "anime" ? "Anime" : "Realistic"
  const g = (images as Record<string, Record<string, string>>)[gKey]
  if (!g) {
    const fallback = (images as Record<string, Record<string, string>>)["Female"]
    return fallback?.[sKey] ?? ""
  }
  return g[sKey] ?? ""
}

// ─── Data Constants ──────────────────────────────────────────────────────────

type StyleOption = {
  value: "realistic" | "anime"
  label: string
  desc: string
}

export const STYLES: StyleOption[] = [
  {
    value: "realistic",
    label: "Realistic",
    desc: "Photorealistic characters with lifelike details",
  },
  {
    value: "anime",
    label: "Anime",
    desc: "Stylized anime-inspired with vibrant expressions",
  },
]

export const ETHNICITY_OPTIONS = [
  {
    name: "Caucasian", value: "Caucasian",
    images: {
      Male: { Anime: "/create/wizard-images/ethnicity/male-anime-caucasian.jpg", Realistic: "/create/wizard-images/ethnicity/male-realistic-caucasian.jpg" },
      Female: { Anime: "/create/wizard-images/ethnicity/female-anime-caucasian.webp", Realistic: "/create/wizard-images/ethnicity/female-realistic-caucasian.webp" },
    },
    videos: ETHNICITY_PREVIEW_VIDEOS.Caucasian,
  },
  {
    name: "Asian", value: "Asian",
    images: {
      Male: { Anime: "/create/wizard-images/ethnicity/male-anime-asian.jpg", Realistic: "/create/wizard-images/ethnicity/male-realistic-asian.jpg" },
      Female: { Anime: "/create/wizard-images/ethnicity/female-anime-asian.webp", Realistic: "/create/wizard-images/ethnicity/female-realistic-asian.webp" },
    },
    videos: ETHNICITY_PREVIEW_VIDEOS.Asian,
  },
  {
    name: "Latina", value: "Latina",
    images: {
      Male: { Anime: "/create/wizard-images/ethnicity/male-anime-latina.jpg", Realistic: "/create/wizard-images/ethnicity/male-realistic-latina.jpg" },
      Female: { Anime: "/create/wizard-images/ethnicity/female-anime-latina.webp", Realistic: "/create/wizard-images/ethnicity/female-realistic-latina.webp" },
    },
    videos: ETHNICITY_PREVIEW_VIDEOS.Latina,
  },
  {
    name: "Black/Afro", value: "African",
    images: {
      Male: { Anime: "/create/wizard-images/ethnicity/male-anime-african.jpg", Realistic: "/create/wizard-images/ethnicity/male-realistic-african.jpg" },
      Female: { Anime: "/create/wizard-images/ethnicity/female-anime-african.webp", Realistic: "/create/wizard-images/ethnicity/female-realistic-african.webp" },
    },
    videos: ETHNICITY_PREVIEW_VIDEOS.African,
  },
  {
    name: "Arab", value: "Arab",
    images: {
      Male: { Anime: "/create/wizard-images/ethnicity/male-anime-arab.jpg", Realistic: "/create/wizard-images/ethnicity/male-realistic-arab.jpg" },
      Female: { Anime: "/create/wizard-images/ethnicity/female-anime-arab.webp", Realistic: "/create/wizard-images/ethnicity/female-realistic-arab.webp" },
    },
    videos: ETHNICITY_PREVIEW_VIDEOS.Arab,
  },
] as const

export const HAIR_STYLE_OPTIONS = [
  {
    name: "Straight", value: "Straight",
    images: {
      Male: { Anime: "/create/wizard-images/hair/male-anime-straight.jpg", Realistic: "/create/wizard-images/hair/male-realistic-straight.jpg" },
      Female: { Anime: "/create/wizard-images/hair/female-anime-straight.webp", Realistic: "/create/wizard-images/hair/female-realistic-straight.webp" },
    },
  },
  {
    name: "Short", value: "pixie cut",
    images: {
      Male: { Anime: "/create/wizard-images/hair/male-anime-short.jpg", Realistic: "/create/wizard-images/hair/male-realistic-short.jpg" },
      Female: { Anime: "/create/wizard-images/hair/female-anime-short.webp", Realistic: "/create/wizard-images/hair/female-realistic-short.webp" },
    },
  },
  {
    name: "Long", value: "waist-length",
    images: {
      Male: { Anime: "/create/wizard-images/hair/male-anime-long.jpg", Realistic: "/create/wizard-images/hair/male-realistic-long.jpg" },
      Female: { Anime: "/create/wizard-images/hair/female-anime-long.webp", Realistic: "/create/wizard-images/hair/female-realistic-long.webp" },
    },
  },
  {
    name: "Ponytail", value: "high ponytail",
    images: {
      Male: { Anime: "/create/wizard-images/hair/male-anime-ponytail.jpg", Realistic: "/create/wizard-images/hair/male-realistic-ponytail.jpg" },
      Female: { Anime: "/create/wizard-images/hair/female-anime-ponytail.webp", Realistic: "/create/wizard-images/hair/female-realistic-ponytail.webp" },
    },
  },
  {
    name: "Bun", value: "Bun",
    images: {
      Male: { Anime: "/create/wizard-images/hair/male-anime-bun.jpg", Realistic: "/create/wizard-images/hair/male-realistic-bun.jpg" },
      Female: { Anime: "/create/wizard-images/hair/female-anime-bun.webp", Realistic: "/create/wizard-images/hair/female-realistic-bun.webp" },
    },
  },
  {
    name: "Curly", value: "voluminous curly",
    images: {
      Male: { Anime: "/create/wizard-images/hair/male-anime-curly.jpg", Realistic: "/create/wizard-images/hair/male-realistic-curly.jpg" },
      Female: { Anime: "/create/wizard-images/hair/female-anime-curly.webp", Realistic: "/create/wizard-images/hair/female-realistic-curly.webp" },
    },
  },
  {
    name: "Braids", value: "Box Braids",
    images: {
      Male: { Anime: "/create/wizard-images/hair/male-anime-braids.jpg", Realistic: "/create/wizard-images/hair/male-realistic-braids.jpg" },
      Female: { Anime: "/create/wizard-images/hair/female-anime-braids.webp", Realistic: "/create/wizard-images/hair/female-realistic-braids.webp" },
    },
  },
] as const

export const HAIR_COLORS = [
  { name: "Black", color: "#1a1a1a", value: "Black" },
  { name: "Blonde", color: "#e6c66e", value: "Blonde" },
  { name: "Brunette", color: "#5c3a21", value: "Brunette" },
  { name: "Redhead", color: "#b5471f", value: "Redhead" },
  { name: "White", color: "#f1f1f1", value: "White" },
  { name: "Silver", color: "#c9ced3", value: "Silver" },
  { name: "Pink", color: "#ff7aa2", value: "Pink" },
  { name: "Blue", color: "#2f80ed", value: "Blue" },
  { name: "Green", color: "#43a86b", value: "Green" },
  { name: "Purple", color: "#9b51e0", value: "Purple" },
] as const

export const EYE_COLORS = [
  { name: "Black", color: "#1a1a1a", value: "Black" },
  { name: "Blue", color: "#2f80ed", value: "Blue" },
  { name: "Brown", color: "#6b4423", value: "Brown" },
  { name: "Green", color: "#3fa45b", value: "Green" },
  { name: "Hazel", color: "#a17a3e", value: "Hazel" },
  { name: "Grey", color: "#9aa1a8", value: "Grey" },
  { name: "Amber", color: "#f5a623", value: "Amber" },
  { name: "Red", color: "#d6453d", value: "Red" },
  { name: "Violet", color: "#7b3ff2", value: "Violet" },
] as const

export const BODY_TYPE_OPTIONS = [
  {
    name: "Slim", value: "slender",
    images: {
      Male: { Anime: "/create/wizard-images/body-type/male-anime-slim.jpg", Realistic: "/create/wizard-images/body-type/male-realistic-slim.jpg" },
      Female: { Anime: "/create/wizard-images/body-type/female-anime-slim.webp", Realistic: "/create/wizard-images/body-type/female-realistic-slim.webp" },
    },
    videos: BODY_TYPE_PREVIEW_VIDEOS.slender,
  },
  {
    name: "Athletic", value: "muscular",
    images: {
      Male: { Anime: "/create/wizard-images/body-type/male-anime-athletic.jpg", Realistic: "/create/wizard-images/body-type/male-realistic-athletic.jpg" },
      Female: { Anime: "/create/wizard-images/body-type/female-anime-athletic.webp", Realistic: "/create/wizard-images/body-type/female-realistic-athletic.webp" },
    },
    videos: BODY_TYPE_PREVIEW_VIDEOS.muscular,
  },
  {
    name: "Voluptuous", value: "BBW",
    images: {
      Male: { Anime: "/create/wizard-images/body-type/male-anime-voluptuous.jpg", Realistic: "/create/wizard-images/body-type/male-realistic-voluptuous.jpg" },
      Female: { Anime: "/create/wizard-images/body-type/female-anime-voluptuous.webp", Realistic: "/create/wizard-images/body-type/female-realistic-voluptuous.webp" },
    },
    videos: BODY_TYPE_PREVIEW_VIDEOS.BBW,
  },
  {
    name: "Curvy", value: "Curvy",
    images: {
      Male: { Anime: "/create/wizard-images/body-type/male-anime-curvy.jpg", Realistic: "/create/wizard-images/body-type/male-realistic-curvy.jpg" },
      Female: { Anime: "/create/wizard-images/body-type/female-anime-curvy.webp", Realistic: "/create/wizard-images/body-type/female-realistic-curvy.webp" },
    },
    videos: BODY_TYPE_PREVIEW_VIDEOS.Curvy,
  },
] as const

export const BREAST_SIZE_OPTIONS = [
  {
    name: "Small", value: "petite",
    images: {
      Female: { Anime: "/create/wizard-images/breast-size/female-anime-small.webp", Realistic: "/create/wizard-images/breast-size/female-realistic-small.webp" },
    },
    videos: BREAST_SIZE_PREVIEW_VIDEOS.petite,
  },
  {
    name: "Medium", value: "Medium",
    images: {
      Female: { Anime: "/create/wizard-images/breast-size/female-anime-medium.webp", Realistic: "/create/wizard-images/breast-size/female-realistic-medium.webp" },
    },
    videos: BREAST_SIZE_PREVIEW_VIDEOS.Medium,
  },
  {
    name: "Large", value: "Large",
    images: {
      Female: { Anime: "/create/wizard-images/breast-size/female-anime-large.webp", Realistic: "/create/wizard-images/breast-size/female-realistic-large.webp" },
    },
    videos: BREAST_SIZE_PREVIEW_VIDEOS.Large,
  },
  {
    name: "Athletic", value: "Athletic",
    images: {
      Female: { Anime: "/create/wizard-images/breast-size/female-anime-athletic.webp", Realistic: "/create/wizard-images/breast-size/female-realistic-athletic.webp" },
    },
    videos: BREAST_SIZE_PREVIEW_VIDEOS.Athletic,
  },
] as const

export const BUTT_SIZE_OPTIONS = [
  {
    name: "Small", value: "Small",
    images: {
      Male: { Anime: "/create/wizard-images/butt-size/male-anime-small.jpg", Realistic: "/create/wizard-images/butt-size/male-realistic-small.jpg" },
      Female: { Anime: "/create/wizard-images/butt-size/female-anime-small.webp", Realistic: "/create/wizard-images/butt-size/female-realistic-small.webp" },
    },
    videos: BUTT_SIZE_PREVIEW_VIDEOS.Small,
  },
  {
    name: "Medium", value: "Medium",
    images: {
      Male: { Anime: "/create/wizard-images/butt-size/male-anime-medium.jpg", Realistic: "/create/wizard-images/butt-size/male-realistic-medium.jpg" },
      Female: { Anime: "/create/wizard-images/butt-size/female-anime-medium.webp", Realistic: "/create/wizard-images/butt-size/female-realistic-medium.webp" },
    },
    videos: BUTT_SIZE_PREVIEW_VIDEOS.Medium,
  },
  {
    name: "Large", value: "Large",
    images: {
      Male: { Anime: "/create/wizard-images/butt-size/male-anime-large.jpg", Realistic: "/create/wizard-images/butt-size/male-realistic-large.jpg" },
      Female: { Anime: "/create/wizard-images/butt-size/female-anime-large.webp", Realistic: "/create/wizard-images/butt-size/female-realistic-large.webp" },
    },
    videos: BUTT_SIZE_PREVIEW_VIDEOS.Large,
  },
  {
    name: "Athletic", value: "Athletic",
    images: {
      Male: { Anime: "/create/wizard-images/butt-size/male-anime-athletic.jpg", Realistic: "/create/wizard-images/butt-size/male-realistic-athletic.jpg" },
      Female: { Anime: "/create/wizard-images/butt-size/female-anime-athletic.webp", Realistic: "/create/wizard-images/butt-size/female-realistic-athletic.webp" },
    },
    videos: BUTT_SIZE_PREVIEW_VIDEOS.Athletic,
  },
] as const

// ─── Personality / Creation Data ─────────────────────────────────────────────

export const PERSONALITY_CHIP_DATA = [
  { value: "flirty", label: "Flirty", emoji: "😏" },
  { value: "caring", label: "Caring", emoji: "💕" },
  { value: "adventurous", label: "Adventurous", emoji: "🔥" },
  { value: "intellectual", label: "Intellectual", emoji: "🧠" },
  { value: "mysterious", label: "Mysterious", emoji: "🌙" },
  { value: "playful", label: "Playful", emoji: "😜" },
  { value: "dominant", label: "Dominant", emoji: "👑" },
  { value: "submissive", label: "Submissive", emoji: "🦋" },
  { value: "confident", label: "Confident", emoji: "💪" },
  { value: "shy", label: "Shy", emoji: "🙈" },
  { value: "romantic", label: "Romantic", emoji: "🌹" },
  { value: "witty", label: "Witty", emoji: "✨" },
  { value: "sarcastic", label: "Sarcastic", emoji: "😈" },
  { value: "nurturing", label: "Nurturing", emoji: "🤗" },
  { value: "bold", label: "Bold", emoji: "⚡" },
  { value: "gentle", label: "Gentle", emoji: "🕊️" },
  { value: "ambitious", label: "Ambitious", emoji: "🚀" },
  { value: "dreamy", label: "Dreamy", emoji: "☁️" },
  { value: "fierce", label: "Fierce", emoji: "🐆" },
  { value: "loyal", label: "Loyal", emoji: "🛡️" },
] as const

export const OCCUPATION_OPTIONS = [
  { value: "none", label: "None", emoji: "🚫" },
  { value: "model", label: "Model", emoji: "📸" },
  { value: "student", label: "Student", emoji: "📚" },
  { value: "nurse", label: "Nurse", emoji: "🏥" },
  { value: "teacher", label: "Teacher", emoji: "👩‍🏫" },
  { value: "artist", label: "Artist", emoji: "🎨" },
  { value: "dancer", label: "Dancer", emoji: "💃" },
  { value: "athlete", label: "Athlete", emoji: "🏋️" },
  { value: "musician", label: "Musician", emoji: "🎵" },
  { value: "chef", label: "Chef", emoji: "👩‍🍳" },
  { value: "scientist", label: "Scientist", emoji: "🔬" },
  { value: "porn_star", label: "Porn Star", emoji: "🔥" },
] as const

export const RELATIONSHIP_OPTIONS = [
  { value: "girlfriend", label: "Girlfriend", emoji: "💕" },
  { value: "friend", label: "Friend", emoji: "🤝" },
  { value: "wife", label: "Wife", emoji: "💍" },
  { value: "step_sister", label: "Step-Sister", emoji: "👧" },
  { value: "colleague", label: "Colleague", emoji: "💼" },
  { value: "neighbor", label: "Neighbor", emoji: "🏡" },
  { value: "stranger", label: "Stranger", emoji: "🌙" },
  { value: "mentor", label: "Mentor", emoji: "📖" },
] as const

export const HOBBY_OPTIONS = [
  { value: "none", label: "None", emoji: "🚫" },
  { value: "yoga", label: "Yoga", emoji: "🧘" },
  { value: "gaming", label: "Gaming", emoji: "🎮" },
  { value: "cooking", label: "Cooking", emoji: "🍳" },
  { value: "dancing", label: "Dancing", emoji: "💃" },
  { value: "reading", label: "Reading", emoji: "📖" },
  { value: "photography", label: "Photography", emoji: "📷" },
  { value: "traveling", label: "Traveling", emoji: "✈️" },
  { value: "fitness", label: "Fitness", emoji: "💪" },
  { value: "music", label: "Music", emoji: "🎶" },
  { value: "art", label: "Art", emoji: "🖌️" },
] as const

export const FETISH_OPTIONS = [
  { value: "none", label: "None", emoji: "🚫" },
  { value: "bdsm", label: "BDSM", emoji: "⛓️" },
  { value: "roleplay", label: "Roleplay", emoji: "🎭" },
  { value: "feet", label: "Feet", emoji: "🦶" },
  { value: "leather", label: "Leather", emoji: "🖤" },
  { value: "cosplay", label: "Cosplay", emoji: "🎀" },
  { value: "voyeur", label: "Voyeur", emoji: "👀" },
  { value: "lingerie", label: "Lingerie", emoji: "👙" },
] as const

export const VOICE_FALLBACK_OPTIONS = [
  { id: "asmr", name: "ASMR", description: "Soft whispered tone, deeply intimate and calming", accent: "american", style: "soft" },
  { id: "sweet", name: "Sweet", description: "Warm and gentle, like a loving embrace in sound", accent: "american", style: "warm" },
  { id: "seductive", name: "Seductive", description: "Sultry and alluring, impossible to ignore", accent: "american", style: "sultry" },
  { id: "playful", name: "Playful", description: "Bubbly and fun, bursting with energy and charm", accent: "american", style: "playful" },
  { id: "confident", name: "Confident", description: "Strong and assertive, commands the room", accent: "british", style: "confident" },
  { id: "gentle", name: "Gentle", description: "Kind and soothing, always makes you feel safe", accent: "american", style: "gentle" },
  { id: "mysterious", name: "Mysterious", description: "Low and enigmatic, with a magnetic allure", accent: "american", style: "calm" },
  { id: "energetic", name: "Energetic", description: "Bright and vivacious, full of infectious positivity", accent: "american", style: "energetic" },
] as const

// ─── Slide config (7 steps) ─────────────────────────────────────────────────

export const SLIDE_LABELS = [
  "basics",
  "ethnicity",
  "hairEyes",
  "body",
  "personality",
  "review",
] as const

export const SLIDE_COUNT = SLIDE_LABELS.length

// ─── Reusable Image Card Grid ────────────────────────────────────────────────

export function ImageCardGrid({
  options,
  selected,
  onSelect,
  gender,
  style,
  columns = 4,
  compact = false,
  customSlot,
}: {
  options: ReadonlyArray<{ name: string; value: string; images: GS | FemaleOnly; videos?: WizardVideoMap }>
  selected: string
  onSelect: (value: string) => void
  gender: string
  style: string
  columns?: number
  compact?: boolean
  /** Renders as last grid item (e.g. Custom card) */
  customSlot?: React.ReactNode
}) {
  const hasSelection = !!selected
  const grid = (
    <div className={cn("grid gap-1.5", {
      "grid-cols-2 sm:grid-cols-3": columns === 3,
      "grid-cols-4 sm:grid-cols-4": columns === 4,
      "grid-cols-4 sm:grid-cols-5": columns === 5 && compact,
      "grid-cols-3 sm:grid-cols-5": columns === 5 && !compact,
    })}>
      {options.map(({ name, value, images, videos }) => {
        const imageUrl = resolveImg(images, gender, style)
        const videoSources = resolveVideo(videos, gender, style)
        const isSelected = selected === value
        const isUnselectedOther = hasSelection && !isSelected
        return (
          <button
            key={value}
            type="button"
            onClick={() => onSelect(value)}
            className={cn(
              "group relative overflow-hidden rounded-[6px] border-2 transition-[opacity,border-color,box-shadow,transform] duration-300 ease-out active:scale-95",
              compact ? "aspect-[2/3] rounded-[6px]" : "aspect-[3/4]",
              "opacity-80",
              hasSelection && isSelected && "opacity-100",
              isUnselectedOther && "opacity-45 hover:opacity-55",
              !hasSelection && "hover:opacity-100",
              isSelected
                ? "border-accent-blue/50 ring-2 ring-accent-blue/30"
                : "border-border/50 hover:border-white/30"
            )}
          >
            {videoSources ? (
              <WizardPreviewVideo
                mp4={videoSources.mp4}
                poster={videoSources.poster}
                label={name}
                playing={isSelected || !hasSelection}
              />
            ) : (
              <Image
                src={imageUrl}
                alt={name}
                fill
                sizes="(max-width: 640px) 33vw, 150px"
                className="object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
            <div className={cn("absolute bottom-0 left-0 right-0 text-center", compact ? "p-1" : "p-2")}>
              <span className={cn(
                "block font-semibold leading-tight",
                compact ? "text-[10px] sm:text-xs" : "text-sm",
                isSelected ? "text-accent-blue" : "text-white"
              )}>{name}</span>
            </div>
            {isSelected && (
              <div
                className={cn("absolute flex animate-in zoom-in-75 fade-in duration-200 items-center justify-center rounded-full bg-accent-blue text-white", compact ? "top-1 right-1 size-3.5" : "top-2 right-2 size-5")}
              >
                <svg className={compact ? "size-2" : "size-3"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </button>
        )
      })}
      {customSlot}
    </div>
  )
  const compactWrapperClass = columns === 4 ? "max-w-xl" : "max-w-2xl"
  return compact ? <div className={cn(compactWrapperClass, "mx-auto")}>{grid}</div> : grid
}

// ─── Color Swatch Picker ─────────────────────────────────────────────────────

function isLightHex(hex: string): boolean {
  const n = parseInt(hex.replace(/^#/, ""), 16)
  const r = (n >> 16) & 0xff
  const g = (n >> 8) & 0xff
  const b = n & 0xff
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.7
}

// Color swatches in a single compact row (all on one line, every size). Tiles are
// short with a small radius so they stay rectangular even when narrow; the
// selected tile springs wider to reveal its name. Shared by Eye Color, Hair Color
// and Skin Tone.
export function ColorSwatchPicker({
  options,
  selected,
  onSelect,
}: {
  options: ReadonlyArray<{ name: string; color: string; value: string }>
  selected: string
  onSelect: (value: string) => void
}) {
  return (
    <div className="flex gap-1">
      {options.map(({ name, color, value }) => {
        const isSelected = selected === value
        const light = isLightHex(color)
        return (
          <button
            key={value}
            type="button"
            onClick={() => onSelect(value)}
            aria-pressed={isSelected}
            aria-label={`${name}${isSelected ? " (selected)" : ""}`}
            style={{ backgroundColor: color }}
            className={cn(
              "relative flex h-7 items-center justify-center overflow-hidden rounded-[5px] px-0.5 outline-none ring-1 ring-inset transition-[flex,transform] duration-200 active:scale-95 sm:h-10",
              "focus-visible:ring-2 focus-visible:ring-primary",
              light ? "ring-black/10" : "ring-white/10",
              isSelected ? "flex-[2.8] ring-2 ring-accent-blue" : "flex-1"
            )}
          >
            {isSelected && (
              <span
                className={cn(
                  "max-w-full animate-in fade-in duration-150 truncate text-[10px] font-bold leading-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] sm:text-xs",
                  light ? "text-black/80" : "text-white"
                )}
              >
                {name}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ─── Step Indicator ──────────────────────────────────────────────────────────

export function SlideIndicator({
  current,
  maxVisitedStep,
  onGoTo,
}: {
  current: number
  maxVisitedStep: number
  onGoTo: (i: number) => void
}) {
  const t = useTranslations("create.wizard")
  const slideLabels = [
    t("steps.basics"),
    t("steps.ethnicity"),
    t("steps.hairEyes"),
    t("steps.body"),
    t("steps.personality"),
    t("steps.review"),
  ] as const

  return (
    <div className="mx-auto flex w-full max-w-md flex-col items-center gap-2.5">
      {/* Segmented progress — one connected bar, fills left→right as you advance.
          Each segment is a clickable target for visited steps. Same on all sizes. */}
      <div className="flex w-full items-center gap-1.5">
        {SLIDE_LABELS.map((_, i) => {
          const localizedLabel = slideLabels[i]
          const isVisited = i <= maxVisitedStep
          const isCurrent = i === current
          return (
            <button
              key={i}
              type="button"
              onClick={() => isVisited && onGoTo(i)}
              disabled={!isVisited}
              aria-label={
                isVisited
                  ? t("stepAria.goTo", { label: localizedLabel })
                  : t("stepAria.locked", { label: localizedLabel })
              }
              aria-current={isCurrent ? "step" : undefined}
              className="group relative flex-1 rounded-full py-2 outline-none disabled:cursor-not-allowed"
            >
              <span className="relative block h-1.5 w-full overflow-hidden rounded-full bg-foreground/[0.12] transition-colors group-enabled:group-hover:bg-foreground/20 group-focus-visible:ring-2 group-focus-visible:ring-primary/50">
                <span
                  className="absolute inset-y-0 left-0 block rounded-full bg-primary"
                  style={{ width: isVisited ? "100%" : "0%" }}
                />
                {isCurrent && (
                  <span className="absolute inset-y-0 left-1/3 block w-1/3 animate-pulse rounded-full bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                )}
              </span>
            </button>
          )
        })}
      </div>

      {/* Current step name + position */}
      <div className="flex items-baseline gap-2">
        <span
          key={current}
          className="animate-in fade-in slide-in-from-bottom-1 duration-200 text-sm font-semibold text-foreground"
        >
          {slideLabels[current]}
        </span>
        <span className="text-xs font-medium text-muted-foreground tabular-nums">
          {t("stepProgress", { current: current + 1, total: SLIDE_COUNT })}
        </span>
      </div>
    </div>
  )
}
